/**
 * @file CustomShippingForm.tsx
 *
 * The custom-shipping (zone) create/edit form, mirroring WSB-v2: a "Shipping zone"
 * section (name + {@link RegionSelector}) and separate "Free shipping" and "Flat
 * rate" sections, each a Gravity {@link ContentContainer} card holding a
 * {@link ShippingMethodSection} (a reorderable table of that type's methods plus
 * an inline add/edit sub-form). Owns the editable state via
 * {@link useCustomShippingFormState}, validates the name on save, and reports the
 * assembled fields to its parent. Cancelling while dirty asks for confirmation.
 */

import { useCallback, useId, useState, type RefObject } from 'react';
import { Link } from 'react-router-dom';
import { ContentContainer, GeneralField, Icon } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../../i18n/messages';
import type { ShippingZoneLocation, ShopContinent } from '../../api/shop-types';
import { ConfirmModal } from '../../components/ConfirmModal';
import { DropdownSelection } from '../../components/DropdownSelection';
import { FormFooter } from '../../components/FormFooter';
import {
  filterContinentsByQuery,
  getShippingRegionLabel,
  isAllRegionsLocation,
} from '../shipping-region-utils';
import { useCustomShippingFormState } from './use-custom-shipping-form-state';
import { RegionSelector } from './RegionSelector';
import { ShippingMethodSection } from './ShippingMethodSection';
import type {
  CustomShippingFormFields,
  MethodFormOption,
  ShippingMethodType,
} from './custom-shipping-form-types';
import styles from './CustomShippingForm.module.css';

/** Which flow the form is serving; drives the title and save label. */
export type CustomShippingFormMode = 'create' | 'edit';

interface CustomShippingFormProps {
  mode: CustomShippingFormMode;
  /** The initial fields (stable reference; the hook re-seeds when it changes). */
  seed: CustomShippingFormFields;
  continents: ShopContinent[];
  currencySymbol: string;
  saving: boolean;
  saveError?: string;
  onSubmit: (fields: CustomShippingFormFields) => void;
  onCancel: () => void;
}

/**
 * Renders the custom-shipping zone form.
 *
 * @param props - Mode, seed fields, region tree, currency, save state, and handlers.
 *
 * @returns The zone form.
 */
export function CustomShippingForm({
  mode,
  seed,
  continents,
  currencySymbol,
  saving,
  saveError,
  onSubmit,
  onCancel,
}: CustomShippingFormProps) {
  const intl = useIntl();
  const nameId = useId();
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [freeFormOpen, setFreeFormOpen] = useState(false);
  const [flatFormOpen, setFlatFormOpen] = useState(false);
  const methodFormOpen = freeFormOpen || flatFormOpen;

  const {
    fields,
    setName,
    setLocations,
    addMethod,
    updateMethod,
    removeMethod,
    setMethods,
    isDirty,
    errors,
    validate,
    firstErrorRef,
  } = useCustomShippingFormState(seed);

  const freeMethods = fields.methods.filter((method) => method.methodType === 'free_shipping');
  const flatMethods = fields.methods.filter((method) => method.methodType === 'flat_rate');

  // Splice a type's reordered subset back into the full method list, leaving the
  // other type's entries in their original slots.
  const reorderType =
    (type: ShippingMethodType) =>
    (reordered: MethodFormOption[]): void => {
      let next = 0;
      setMethods(fields.methods.map((method) => (method.methodType === type ? reordered[next++] : method)));
    };

  // The all-regions sentinel has no entry in the tree; label it "Everywhere".
  const regionLabel = useCallback(
    (location: ShippingZoneLocation): string =>
      isAllRegionsLocation(location)
        ? intl.formatMessage(adminMessages.shippingEverywhere)
        : getShippingRegionLabel(location, continents),
    [continents, intl],
  );

  const handleSave = (): void => {
    if (!validate()) {
      return;
    }
    onSubmit(fields);
  };

  const handleCancel = (): void => {
    if (isDirty) {
      setConfirmDiscard(true);
      return;
    }
    onCancel();
  };

  const title =
    mode === 'edit' ? adminMessages.shippingZoneEditTitle : adminMessages.shippingZoneAddTitle;

  return (
    <div className={styles.form}>
      <header className="gv-flex gv-flex-col gv-w-full gv-gap-sm">
        <div className="gv-breadcrumbs">
          <Link to="/shipping">
            <Icon name="arrow_back" size="small" />
            <FormattedMessage {...adminMessages.formBack} />
          </Link>
        </div>
        <h2 className="gv-heading-lg gv-m-0">
          <FormattedMessage {...title} />
        </h2>
      </header>

      <div className={`gv-flex gv-flex-col gv-gap-lg gv-w-full ${styles.column}`}>
        {/* Shipping zone */}
        <div className="gv-flex gv-flex-col gv-gap-md">
          <div className="gv-flex gv-flex-col gv-gap-xs">
            <div className="gv-text-lg gv-text-bold">
              <FormattedMessage {...adminMessages.shippingZoneSectionTitle} />
            </div>
            <div className="gv-text-sm">
              <FormattedMessage {...adminMessages.shippingZoneSectionDescription} />
            </div>
          </div>
          <ContentContainer className="gv-p-lg gv-flex gv-flex-col gv-gap-md">
            <div className="gv-form-option gv-w-full">
              <label className="gv-label" htmlFor={nameId}>
                <FormattedMessage {...adminMessages.shippingZoneName} />
              </label>
              <GeneralField
                id={nameId}
                inputRef={firstErrorRef as RefObject<HTMLInputElement>}
                value={fields.name}
                error={errors.nameError}
                data-testid="shipping-zone-form-name"
                placeholder={intl.formatMessage(adminMessages.shippingZoneNamePlaceholder)}
                aria-label={intl.formatMessage(adminMessages.shippingZoneName)}
                onChange={(event) => setName(event.target.value)}
              />
            </div>

            <div className="gv-form-option gv-w-full">
              <span className="gv-label">
                <FormattedMessage {...adminMessages.shippingZoneRegions} />
              </span>
              <DropdownSelection
                selected={fields.locations}
                getLabel={regionLabel}
                onSave={setLocations}
                searchFn={(query) => filterContinentsByQuery(continents, query)}
                searchLabel={intl.formatMessage(adminMessages.shippingRegionSearchLabel)}
                testIdPrefix="shipping-zone-form-regions"
                renderDropdown={(data, query, _clearQuery, draft, setDraft, save, cancel) => (
                  <RegionSelector
                    data={data}
                    selected={draft}
                    setSelected={setDraft}
                    searchQuery={query}
                    onSave={save}
                    onCancel={cancel}
                  />
                )}
              />
              <p className="gv-caption-lg">
                <FormattedMessage {...adminMessages.shippingRegionsHelp} />
              </p>
            </div>
          </ContentContainer>
        </div>

        {/* Free shipping */}
        <div className="gv-flex gv-flex-col gv-gap-md">
          <div className="gv-text-lg gv-text-bold">
            <FormattedMessage {...adminMessages.shippingMethodFreeShipping} />
          </div>
          <ContentContainer className="gv-p-lg gv-flex gv-flex-col gv-gap-md">
            <div className="gv-text-sm">
              <FormattedMessage {...adminMessages.shippingFreeShippingDescription} />
            </div>
            <ShippingMethodSection
              methodType="free_shipping"
              methods={freeMethods}
              currencySymbol={currencySymbol}
              testIdPrefix="free-shipping-methods"
              dragHandleAriaLabel={intl.formatMessage(adminMessages.shippingDragToReorder)}
              onAdd={addMethod}
              onUpdate={updateMethod}
              onRemove={removeMethod}
              onReorder={reorderType('free_shipping')}
              onEditingChange={setFreeFormOpen}
            />
          </ContentContainer>
        </div>

        {/* Flat rate */}
        <div className="gv-flex gv-flex-col gv-gap-md">
          <div className="gv-text-lg gv-text-bold">
            <FormattedMessage {...adminMessages.shippingMethodFlatRate} />
          </div>
          <ContentContainer className="gv-p-lg gv-flex gv-flex-col gv-gap-md">
            <div className="gv-text-sm">
              <FormattedMessage {...adminMessages.shippingFlatRateDescription} />
            </div>
            <ShippingMethodSection
              methodType="flat_rate"
              methods={flatMethods}
              currencySymbol={currencySymbol}
              testIdPrefix="flat-rate-methods"
              dragHandleAriaLabel={intl.formatMessage(adminMessages.shippingDragToReorder)}
              onAdd={addMethod}
              onUpdate={updateMethod}
              onRemove={removeMethod}
              onReorder={reorderType('flat_rate')}
              onEditingChange={setFlatFormOpen}
            />
          </ContentContainer>
        </div>

        {saveError && (
          <p
            className="gv-text-sm gv-state-critical"
            role="alert"
            data-testid="shipping-zone-form-error"
          >
            {saveError}
          </p>
        )}

        <FormFooter>
          <button
            type="button"
            className="gv-button gv-button-cancel"
            data-testid="shipping-zone-form-cancel"
            onClick={handleCancel}
            disabled={saving || methodFormOpen}
          >
            <FormattedMessage {...adminMessages.formBack} />
          </button>
          <button
            type="button"
            className="gv-button gv-button-primary"
            data-testid="shipping-zone-form-save"
            onClick={handleSave}
            disabled={saving || methodFormOpen}
          >
            {saving ? (
              <FormattedMessage {...adminMessages.shippingSaving} />
            ) : (
              <FormattedMessage {...adminMessages.shippingZoneSave} />
            )}
          </button>
        </FormFooter>
      </div>

      {confirmDiscard && (
        <ConfirmModal
          title={intl.formatMessage(adminMessages.shippingDiscardTitle)}
          message={intl.formatMessage(adminMessages.shippingDiscardMessage)}
          confirmLabel={intl.formatMessage(adminMessages.shippingDiscard)}
          cancelLabel={intl.formatMessage(adminMessages.shippingKeepEditing)}
          loadingLabel={intl.formatMessage(adminMessages.shippingDiscard)}
          confirmVariant="destructive"
          onPrimaryClick={() => {
            setConfirmDiscard(false);
            onCancel();
          }}
          onSecondaryClick={() => setConfirmDiscard(false)}
          onClose={() => setConfirmDiscard(false)}
        />
      )}
    </div>
  );
}
